/* Retro Island — Wishlist (Dawn only)
 *
 * Lets anyone search any game (reusing the Instant Trade Quote catalog search),
 * pick bracket-edition sub-versions + conditions (Loose / In Box / CIB / New, with the
 * post-2011 "In Box -> CIB (Has Case)" merge), and subscribe to an in-stock email alert.
 *
 * Alerts need a store sign-in (owner, 2026-10-22). Who the list belongs to is one of:
 * - account: signed in; games save to the account and alerts go to its email.
 * - link: opened from a wishlist email's Manage link (lists saved before sign-in was required).
 * - device: neither; games wait on this device, marked "Alerts not on yet", until sign-in carries them up.
 * The on-page list is mirrored to localStorage, and only rows this device added stay when its owner changes.
 */
(function () {
  'use strict';

  var CFG = window.__RIW__ || {};
  var EP = CFG.endpoints || {};
  var CUST = CFG.customer || { loggedIn: false, email: '', id: '' };
  var LS_KEY = 'ri:wishlist:v1';
  var LS_OWNER_KEY = 'ri:wishlist:owner';
  var SS_MANAGE_KEY = 'ri:wishlist:manage';
  var LS_MARKETING_KEY = 'ri:wishlist:marketing-opt-in';
  /* The subscribe route takes at most 25 games a request. */
  var CARRY_BATCH = 25;
  var SEARCH_DEBOUNCE_MS = 280;
  var searchCache = {};
  var searchAbort = null;
  var searchGen = 0;

  var root = document.getElementById('riwRoot');
  if (!root) return;

  var els = {
    search: document.getElementById('riwSearch'),
    searchDd: document.getElementById('riwSearchDd'),
    builder: document.getElementById('riwBuilder'),
    list: document.getElementById('riwList'),
    toast: document.getElementById('riwToast'),
    banner: document.getElementById('riwBanner'),
    prefs: document.getElementById('riwPrefs'),
    who: document.getElementById('riwWho'),
    listCount: document.getElementById('riwListCount')
  };

  /* ---------- small utils ---------- */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeQuery(raw) {
    var s = (raw || '').trim();
    if (!s) return '';
    s = s.split(/\s+/).join(' ');
    try {
      if (String.prototype.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) {}
    return s;
  }

  function stripBrackets(title) {
    return String(title || '').replace(/\[[^\]]*\]/g, '').replace(/\s+/g, ' ').trim();
  }

  function baseName(title) {
    return normalizeQuery(stripBrackets(title)).toLowerCase();
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      if (t) clearTimeout(t);
      t = setTimeout(function () {
        t = null;
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
  }

  function toast(msg) {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () {
      els.toast.hidden = true;
    }, 5200);
  }

  function headerOffset() {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  }

  /** Brings el just under the sticky site header. With onlyIfNeeded, an element already fully on screen stays put. */
  function scrollUnderHeader(el, onlyIfNeeded) {
    if (!el || !el.getBoundingClientRect) return;
    var rect = el.getBoundingClientRect();
    var headerH = headerOffset();
    if (onlyIfNeeded && rect.top >= headerH && rect.bottom <= window.innerHeight) return;
    var top = rect.top + window.pageYOffset - headerH - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function showBanner(kind, html) {
    if (!els.banner) return;
    els.banner.className = 'riw-banner riw-banner--' + (kind || 'info');
    els.banner.innerHTML = html;
    els.banner.hidden = false;
  }

  function hideBanner() {
    if (!els.banner) return;
    els.banner.hidden = true;
    els.banner.innerHTML = '';
  }

  /** A yes ticked on this device before sign-in was required; sent along when its games are carried up. */
  function getMarketingOptIn() {
    try {
      return localStorage.getItem(LS_MARKETING_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  /* ---------- whose list this is ---------- */

  /** The email a Manage link names, once the server says (see loadPrefsPanel). */
  var linkEmail = '';

  function listMode() {
    if (CUST.loggedIn) return 'account';
    if (getManageToken()) return 'link';
    return 'device';
  }

  function alertEmail() {
    if (listMode() === 'account') return String(CUST.email || '').trim();
    if (listMode() === 'link') return linkEmail;
    return '';
  }

  /**
   * Rows that came from a server belong to whoever was signed in (or whose email link was open) when they were
   * fetched. A shopper who signed out, or a different account on a shared computer, must not see them, and a
   * guest must not be told "Alerts go to" an account email the page once saved. Games this device added and
   * never saved anywhere stay, so signing in can carry them up.
   */
  function claimLocalMirror() {
    try {
      localStorage.removeItem('ri:wishlist:email');
      localStorage.removeItem('ri:wishlist:pending-confirm');
    } catch (e) {}
    var manage = getManageToken();
    var owner = CUST.loggedIn ? 'c:' + (CUST.id || CUST.email) : manage ? 'm:' + manage : '';
    var prev = '';
    try {
      prev = localStorage.getItem(LS_OWNER_KEY) || '';
    } catch (e) {}
    if (!owner || owner !== prev) {
      saveLocal(loadLocal().filter(function (e) {
        return !e.synced;
      }));
    }
    try {
      localStorage.setItem(LS_OWNER_KEY, owner);
    } catch (e) {}
  }

  /**
   * Shopify's sign-in (an emailed code, no password) that comes back to this page. return_to must be relative;
   * one-time flags and the email-link token are left off.
   */
  function signInUrl(hint) {
    var p = new URLSearchParams(window.location.search);
    ['manage', 'manage_token', 'confirmed', 'unsubscribed', 'prefs', 'preferences', 'q'].forEach(function (k) {
      p.delete(k);
    });
    var back = window.location.pathname + (p.toString() ? '?' + p.toString() : '');
    var base = CFG.signInPath || '/customer_authentication/login';
    return base + '?return_to=' + encodeURIComponent(back) + (hint ? '&login_hint=' + encodeURIComponent(hint) : '');
  }

  function waitingEntries() {
    return loadLocal().filter(function (e) {
      return !e.synced;
    });
  }

  function handleQueryFlags() {
    try {
      var p = new URLSearchParams(window.location.search);
      // Persist manage token before stripping query flags.
      getManageToken();
      if (p.get('confirmed') === '1') {
        // An older confirm email still lands here with its Manage link.
        showBanner(
          'success',
          '<strong>Email confirmed!</strong> Your wishlist alerts are on. We\'ll email you when a matching game is in stock.'
        );
        p.delete('confirmed');
        var clean = window.location.pathname + (p.toString() ? '?' + p.toString() : '') + window.location.hash;
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', clean);
        }
        return;
      }
      if (p.get('unsubscribed') === '1') {
        showBanner(
          'info',
          '<strong>Wishlist alerts are off.</strong> You won\'t get stock emails. This does not change marketing preferences - use Email preferences on your manage link to control those.'
        );
        toast('Unsubscribed from wishlist stock alerts.');
        p.delete('unsubscribed');
        var clean2 = window.location.pathname + (p.toString() ? '?' + p.toString() : '') + window.location.hash;
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', clean2);
        }
      }
    } catch (e) {}
  }

  function wantsPrefsModal() {
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get('prefs') === '1' || p.get('preferences') === '1';
    } catch (e) {
      return false;
    }
  }

  function closePrefsModal() {
    var el = document.getElementById('riwPrefsModal');
    if (el && el.parentNode) el.parentNode.removeChild(el);
    document.body.classList.remove('riw-prefs-open');
  }

  function openPrefsModal(innerHtml) {
    closePrefsModal();
    var overlay = document.createElement('div');
    overlay.id = 'riwPrefsModal';
    overlay.className = 'riw-prefs-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Email preferences');
    overlay.innerHTML =
      '<div class="riw-prefs-modal-backdrop" data-riw-prefs-close="1"></div>' +
      '<div class="riw-prefs-modal-panel">' +
      '<button type="button" class="riw-prefs-modal-close" data-riw-prefs-close="1" aria-label="Close">&times;</button>' +
      '<div class="riw-prefs-modal-body">' +
      innerHtml +
      '</div></div>';
    document.body.appendChild(overlay);
    document.body.classList.add('riw-prefs-open');
    overlay.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.getAttribute && t.getAttribute('data-riw-prefs-close')) closePrefsModal();
    });
    document.addEventListener(
      'keydown',
      function onKey(ev) {
        if (ev.key === 'Escape') {
          closePrefsModal();
          document.removeEventListener('keydown', onKey);
        }
      },
      { once: true }
    );
  }

  function prefsFormHtml(state) {
    return (
      '<div class="riw-prefs-card riw-prefs-card--modal">' +
      '<h2 class="riw-prefs-title">Email preferences</h2>' +
      '<p class="riw-prefs-lead">These are two separate choices for <strong>' +
      escapeHtml(state.email || '') +
      '</strong>. Turning wishlist stock alerts off pauses your list so you stop getting mail. Turning them back on brings those games back.</p>' +
      '<label class="riw-prefs-row">' +
      '<input type="checkbox" id="riwPrefWishlist" ' +
      (state.wishlistAlertsEnabled ? 'checked ' : '') +
      '>' +
      '<span><strong>Wishlist stock alerts</strong><span class="riw-prefs-desc">Email me when a wishlisted game is in stock.</span></span>' +
      '</label>' +
      '<label class="riw-prefs-row">' +
      '<input type="checkbox" id="riwPrefMarketing" ' +
      (state.marketingOptIn ? 'checked ' : '') +
      '>' +
      '<span><strong>Marketing emails</strong><span class="riw-prefs-desc">News, offers, and store updates (optional). Never assumed.</span></span>' +
      '</label>' +
      '<button type="button" class="riw-submit button" id="riwPrefSave">Save preferences</button>' +
      '<p class="riw-prefs-status" id="riwPrefStatus" role="status"></p>' +
      '</div>'
    );
  }

  function bindPrefsSave() {
    var save = document.getElementById('riwPrefSave');
    if (!save) return;
    save.addEventListener('click', function () {
      var status = document.getElementById('riwPrefStatus');
      var wl = document.getElementById('riwPrefWishlist');
      var mk = document.getElementById('riwPrefMarketing');
      if (status) status.textContent = 'Saving…';
      fetch(EP.preferences, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          manageToken: getManageToken(),
          wishlistAlertsEnabled: !!(wl && wl.checked),
          marketingOptIn: !!(mk && mk.checked)
        })
      })
        .then(function (r) {
          return r.ok ? r.json() : Promise.reject(r.status);
        })
        .then(function (data) {
          if (status) status.textContent = 'Saved.';
          toast('Email preferences saved.');
          if (data && data.wishlistAlertsEnabled === false) {
            showBanner(
              'info',
              '<strong>Wishlist alerts are off</strong> for this email. Your list is paused. Turn them back on here anytime to bring those games back.'
            );
          } else {
            hideBanner();
          }
          syncFromServer();
        })
        .catch(function () {
          if (status) status.textContent = 'Could not save. Try again.';
        });
    });
  }

  function renderPrefsPanel(state) {
    var html = prefsFormHtml(state);
    if (wantsPrefsModal()) {
      openPrefsModal(html);
      bindPrefsSave();
      return;
    }
    if (!els.prefs) return;
    els.prefs.hidden = false;
    els.prefs.innerHTML =
      '<details class="riw-prefs-fold"><summary class="riw-prefs-summary">Email preferences</summary>' +
      html +
      '</details>';
    bindPrefsSave();
  }

  function prefsLinkErrorHtml() {
    return (
      '<div class="riw-prefs-card riw-prefs-card--modal">' +
      '<h2 class="riw-prefs-title">Link expired or invalid</h2>' +
      '<p class="riw-prefs-lead">This preferences link isn\'t valid anymore. Open <strong>Email preferences &amp; unsubscribe</strong> from a real wishlist email (after you\'ve confirmed your address), or use <strong>Manage wishlist</strong> from that email.</p>' +
      '</div>'
    );
  }

  /**
   * A manage link names one inbox. Without one, a signed-in shopper gets the
   * preferences for their account email (the server looks it up), on any device.
   */
  function loadPrefsPanel() {
    var manage = getManageToken();
    var asModal = wantsPrefsModal();
    var viaAccount = !manage && CUST.loggedIn;
    if (!manage && !viaAccount) {
      if (asModal) openPrefsModal(prefsLinkErrorHtml());
      return;
    }
    if (!EP.preferences) {
      if (asModal) openPrefsModal(prefsLinkErrorHtml());
      return;
    }
    if (asModal) {
      openPrefsModal(
        '<div class="riw-prefs-card riw-prefs-card--modal"><p class="riw-prefs-lead">Loading preferences…</p></div>'
      );
    }
    fetch(manage ? EP.preferences + '?manage=' + encodeURIComponent(manage) : EP.preferences)
      .then(function (r) {
        return r.json().then(function (data) {
          return { okHttp: r.ok, status: r.status, data: data };
        });
      })
      .then(function (pack) {
        if (pack && pack.okHttp && pack.data && pack.data.ok) {
          if (manage && !viaAccount) {
            linkEmail = String(pack.data.email || '').trim();
            renderWho();
          }
          renderPrefsPanel(pack.data);
          return;
        }
        if (manage && !viaAccount && pack && pack.status === 403) forgetManageLink();
        if (asModal) openPrefsModal(prefsLinkErrorHtml());
        else if (!viaAccount && pack && pack.status === 403)
          showBanner(
            'info',
            '<strong>That wishlist link has expired.</strong> Sign in with your email to see your list and turn alerts on.'
          );
      })
      .catch(function () {
        if (asModal) openPrefsModal(prefsLinkErrorHtml());
      });
  }

  function getManageToken() {
    try {
      try {
        localStorage.removeItem('ri:wishlist:manage');
      } catch (e1) {}
      var p = new URLSearchParams(window.location.search);
      var fromUrl = p.get('manage') || p.get('manage_token') || '';
      if (fromUrl) {
        try {
          sessionStorage.setItem(SS_MANAGE_KEY, fromUrl);
        } catch (e2) {}
        return fromUrl;
      }
      return sessionStorage.getItem(SS_MANAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  /** A Manage link the server no longer knows: drop it, so the page falls back to this device's list. */
  function forgetManageLink() {
    try {
      sessionStorage.removeItem(SS_MANAGE_KEY);
      var p = new URLSearchParams(window.location.search);
      p.delete('manage');
      p.delete('manage_token');
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, '', window.location.pathname + (p.toString() ? '?' + p.toString() : '') + window.location.hash);
      }
    } catch (e) {}
    linkEmail = '';
    claimLocalMirror();
    renderList();
    renderWho();
  }

  function coverThumbUrl(rawUrl, width) {
    var u = String(rawUrl || '').trim();
    if (!u || u.indexOf('data:') === 0) return u;
    try {
      var parsed = new URL(u, window.location.origin);
      var h = parsed.hostname;
      if (h.indexOf('cdn.shopify.com') === -1 && h.indexOf('shopifycdn.net') === -1) return u;
      parsed.searchParams.set('width', String(width || 200));
      return parsed.toString();
    } catch (e) {
      return u;
    }
  }

  function resultImage(item) {
    if (!item) return '';
    var raw = item.imgUrl || item.imageUrl || item.image || '';
    if (raw && typeof raw === 'object') raw = raw.url || raw.src || '';
    return coverThumbUrl(raw);
  }

  // Same request as Instant Trade Quote. mode=wishlist skips Shopify covers on the live search.
  function searchGames(q) {
    var key = normalizeQuery(q);
    if (!key) return Promise.resolve([]);
    if (searchCache[key]) return Promise.resolve(searchCache[key]);
    if (searchAbort) {
      try {
        searchAbort.abort();
      } catch (e) {}
    }
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    searchAbort = ctrl;
    var url = EP.search + '?q=' + encodeURIComponent(key);
    return fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (r) {
        // A busy (429) or failed search is not "No matches", and must not be cached as one.
        if (!r.ok) throw new Error('search ' + r.status);
        return r.json();
      })
      .then(function (d) {
        if (ctrl && searchAbort !== ctrl) return [];
        var res = d && d.results ? d.results : [];
        searchCache[key] = res;
        return res;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return [];
        throw err;
      });
  }

  /* ---------- platform pill (ported from snippets/ri-platform-pill.liquid) ---------- */

  function platformPillMod(system) {
    var s = String(system || '').toLowerCase();
    function has(x) {
      return s.indexOf(x) !== -1;
    }
    if (has('64')) return 'n64';
    if (has('color') || has('gbc')) return 'gbc';
    if (has('advance') || has('gba')) return 'gba';
    if (has('gamecube')) return 'gamecube';
    if (has('3ds')) return 'nintendo';
    if (has('ds')) return 'ds';
    if (has('wii u')) return 'wiiu';
    if (has('wii')) return 'wii';
    if (has('ps5') || has('playstation 5')) return 'ps5';
    if (has('ps4') || has('playstation 4') || has('psp') || has('vita')) return 'ps4';
    if (has('ps3') || has('playstation 3')) return 'ps3';
    if (has('ps2') || has('playstation 2')) return 'ps2';
    if (s === 'playstation' || s === 'ps1' || s === 'psx') return 'ps1';
    if (has('xbox')) return 'xbox';
    if (has('dreamcast')) return 'dreamcast';
    if (has('sega') || has('genesis') || has('saturn')) return 'sega';
    if (has('atari')) return 'atari';
    if (has('snes') || has('super nintendo') || has('super famicom') || has('super nes')) return 'snes';
    if (has('nintendo') || has('switch') || has('nes') || has('famicom') || has('mario')) return 'nintendo';
    return 'default';
  }

  function platformPillHtml(system) {
    if (!system) return '';
    return (
      '<span class="ri-platform-pill ri-platform-pill--' +
      platformPillMod(system) +
      '">' +
      escapeHtml(system) +
      '</span>'
    );
  }

  /* ---------- condition model (ported from ITQ) ---------- */

  function releaseYear(item) {
    if (!item || item.releaseYear == null || item.releaseYear === '') return null;
    var y = Number(item.releaseYear);
    if (isNaN(y) || y < 1900 || y > 2100) return null;
    return y;
  }

  function isModernYear(year) {
    return year != null && year > 2011;
  }

  function normPlatform(system) {
    return String(system || '')
      .toLowerCase()
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  var PLASTIC_CASE_EXACT = {
    'nintendo 3ds': true,
    'nintendo 2ds': true,
    'nintendo ds': true,
    'nintendo dsi': true,
    'nintendo switch': true,
    'nintendo switch 2': true,
    'playstation vita': true
  };

  function isPlasticCasePlatform(system) {
    var s = normPlatform(system);
    if (!s) return false;
    if (PLASTIC_CASE_EXACT[s]) return true;
    function has(sub) {
      return s.indexOf(sub) !== -1;
    }
    if (has('playstation vita') || has('ps vita') || has('psvita')) return true;
    if (/\b3ds\b/.test(s) || /\b2ds\b/.test(s)) return true;
    if (has('nintendo 3ds') || has('new 3ds')) return true;
    if (has('nintendo ds') || has('nintendo dsi')) return true;
    if (has('nintendo switch') || has('switch 2') || has('switch lite')) return true;
    if (has('nintendo') && /\bswitch\b/.test(s)) return true;
    return false;
  }

  function looseLabel(system) {
    var s = normPlatform(system);
    function has(sub) {
      return s.indexOf(sub) !== -1;
    }
    if (has('playstation vita') || has('ps vita') || has('psvita')) return 'Cartridge Only';
    var discKw = [
      'wii u', 'gamecube', 'game cube', 'playstation 5', 'playstation 4', 'playstation 3',
      'playstation 2', 'playstation', 'psp', 'umd', 'xbox', 'dreamcast', 'sega cd', 'mega cd',
      'segacd', 'sega saturn', 'neo geo cd', 'neogeocd', '3do', 'cd-i', 'turbografx-cd',
      'pc-engine cd', 'pc engine cd', 'philips cd-i'
    ];
    for (var i = 0; i < discKw.length; i++) {
      if (has(discKw[i])) return 'Disc Only';
    }
    if (has('wii') && !has('wii u')) return 'Disc Only';
    if (/\bps\s*[1-5]\b/.test(s)) return 'Disc Only';
    if (isPlasticCasePlatform(system)) return 'Cartridge Only';
    var cartKw = [
      'nintendo switch', 'switch 2', 'switch lite', 'new 3ds', 'nintendo 3ds', 'nintendo ds',
      'nintendo dsi', '2ds', 'game boy', 'gameboy', 'virtual boy', 'nes', 'famicom', 'snes',
      'super nintendo', 'nintendo 64', 'n64', 'game gear', 'genesis', 'mega drive',
      'master system', '32x', 'neo geo', 'neo-geo', 'aes', 'turbografx-16', 'turbografx',
      'pc engine', 'tg-16', 'atari', 'lynx', 'jaguar', 'intellivision', 'colecovision', 'wonderswan'
    ];
    for (var j = 0; j < cartKw.length; j++) {
      if (has(cartKw[j])) return 'Cartridge Only';
    }
    return 'Game Only';
  }

  function isCartridgeSystem(system) {
    return looseLabel(system) === 'Cartridge Only';
  }

  function inBoxLabel(system) {
    if (isCartridgeSystem(system) && !isPlasticCasePlatform(system)) return 'Has Box';
    return 'Has Case';
  }

  function cibLabel(system) {
    if (isCartridgeSystem(system) && !isPlasticCasePlatform(system)) return 'Has Box & Manual';
    return 'Has Case & Manual';
  }

  // Canonical condition keys sent to the backend, with display labels.
  // Modern (2012+) collapses the boxed tier to a single "Has Case" (key CIB).
  function conditionOptions(system, modern) {
    var opts = [{ key: 'Loose', label: looseLabel(system) }];
    if (modern) {
      opts.push({ key: 'CIB', label: inBoxLabel(system) });
    } else {
      opts.push({ key: 'In Box', label: inBoxLabel(system) });
      opts.push({ key: 'CIB', label: cibLabel(system) });
    }
    opts.push({ key: 'New', label: 'Sealed New' });
    return opts;
  }

  function conditionLabelMap(system, modern) {
    return conditionOptions(system, modern).reduce(function (m, o) {
      m[o.key] = o.label;
      return m;
    }, {});
  }

  /* ---------- localStorage mirror ---------- */

  // A wishlist entry groups every bracket version of one game+platform under a
  // single stable id, so re-releases (Greatest Hits, Nintendo Selects, ...) are
  // one item in the UI. Any ticked edition that fully matches satisfies that row.
  function keyFor(gameName, platform) {
    return baseName(gameName) + '|' + normPlatform(platform);
  }

  function uniq(arr) {
    var out = [];
    (arr || []).forEach(function (v) {
      if (v != null && out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  function normalizeEntry(e) {
    if (!e) return e;
    if (!Array.isArray(e.pcids)) e.pcids = e.pcid ? [e.pcid] : [];
    if (!e.id) e.id = keyFor(e.gameName || '', e.platformName || '');
    // Rows saved before the synced flag: a server group id means it came from a server.
    if (e.synced == null && e.groupId && String(e.groupId).indexOf('local-') !== 0) e.synced = true;
    return e;
  }

  function loadLocal() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      return arr.map(normalizeEntry);
    } catch (e) {
      return [];
    }
  }

  function saveLocal(entries) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(entries));
    } catch (e) {}
  }

  function upsertGroup(entry) {
    var entries = loadLocal();
    var i = entries.findIndex(function (e) {
      return e.id === entry.id;
    });
    if (i >= 0) {
      var ex = entries[i];
      ex.pcids = uniq((ex.pcids || []).concat(entry.pcids || []));
      ex.conditions = uniq((ex.conditions || []).concat(entry.conditions || []));
      ex.conditionLabels = uniq((ex.conditionLabels || []).concat(entry.conditionLabels || []));
      ex.versions = (ex.versions || []).concat(entry.versions || []).filter(function (v, idx, a) {
        return a.findIndex(function (x) { return x.pcid === v.pcid; }) === idx;
      });
      ex.versionTotal = Math.max(ex.versionTotal || 0, entry.versionTotal || 0);
      ex.condTotal = Math.max(ex.condTotal || 0, entry.condTotal || 0);
      ex.gameName = entry.gameName || ex.gameName;
      if (entry.imgUrl) ex.imgUrl = entry.imgUrl;
      entries[i] = ex;
    } else {
      entries.unshift(entry);
    }
    saveLocal(entries);
    return entries;
  }

  function removeLocal(id) {
    var entries = loadLocal().filter(function (e) {
      return e.id !== id;
    });
    saveLocal(entries);
    return entries;
  }

  // The last add or edit still on its way to the server. A remove waits for it, so a quick
  // add-then-remove cannot land in the wrong order and leave the game alerting.
  var pendingSave = Promise.resolve();

  /** Resolves 'ok', 'denied' (no sign-in or manage link this visit), or 'failed'. Never rejects. */
  function removeOnServer(id, pcids) {
    if (!EP.remove) return Promise.resolve('ok');
    return pendingSave
      .catch(function () {})
      .then(function () {
        return fetch(EP.remove, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id, pcids: pcids || [], manageToken: getManageToken() })
        });
      })
      .then(function (r) {
        if (r.ok) return 'ok';
        return r.status === 403 ? 'denied' : 'failed';
      })
      .catch(function () {
        return 'failed';
      });
  }

  /* ---------- search ---------- */

  function trackedPcidSet() {
    var set = {};
    loadLocal().forEach(function (e) {
      (e.pcids || []).forEach(function (p) {
        set[p] = true;
      });
    });
    return set;
  }

  function searchThumbHtml(item) {
    var src = resultImage(item);
    if (!src) return '<span class="riw-dd-img riw-dd-img--empty"></span>';
    return '<span class="riw-dd-img"><img alt="" src="' + escapeHtml(src) + '" decoding="async"></span>';
  }

  function searchRowHtml(item, idx, tracked) {
    var title = escapeHtml(item.title || item.gameName || '');
    var system = item.system || item.platformName || '';
    var isTracked = !!(tracked && item.pcid && tracked[item.pcid]);

    var flags =
      (item.inStock ? '<span class="riw-dd-stock">In stock</span>' : '') +
      (isTracked ? '<span class="riw-dd-flag">In wishlist</span>' : '');

    return (
      '<button type="button" class="riw-dd-item' +
      (isTracked ? ' is-tracked' : '') +
      (item.inStock ? ' is-instock' : '') +
      '" data-idx="' +
      idx +
      '">' +
      searchThumbHtml(item) +
      '<span class="riw-dd-text"><span class="riw-dd-main">' +
      title +
      flags +
      '</span>' +
      platformPillHtml(system) +
      '</span></button>'
    );
  }

  var lastResults = [];

  function ddBox() {
    if (!els.searchDd) return null;
    var box = els.searchDd.querySelector('.riw-dropdown__scroll');
    if (box) return box;
    box = document.createElement('div');
    box.className = 'riw-dropdown__scroll';
    els.searchDd.appendChild(box);
    return box;
  }

  function closeDropdown() {
    els.searchDd.hidden = true;
  }

  function renderResults(results) {
    lastResults = results;
    var box = ddBox();
    if (!box) return;
    if (!results.length) {
      box.innerHTML = '<div class="riw-dd-empty">No matches. Try another name or system.</div>';
      return;
    }
    var tracked = trackedPcidSet();
    box.innerHTML = results
      .map(function (it, i) {
        return searchRowHtml(it, i, tracked);
      })
      .join('');
  }

  var runSearch = debounce(function () {
    var q = normalizeQuery(els.search.value);
    var gen = (searchGen += 1);
    if (!q) {
      lastResults = [];
      if (ddBox()) ddBox().innerHTML = '';
      els.searchDd.classList.remove('is-stale');
      closeDropdown();
      return;
    }
    els.searchDd.hidden = false;
    if (searchCache[q]) {
      els.searchDd.classList.remove('is-stale');
      renderResults(searchCache[q]);
      return;
    }
    // Keep the rows that are already up. The moving line is only for an empty panel.
    if (els.searchDd.querySelector('.riw-dd-item')) {
      els.searchDd.classList.add('is-stale');
    } else if (!els.searchDd.querySelector('.riw-dd-load')) {
      ddBox().innerHTML =
        '<div class="riw-dd-load"><span class="riw-dd-bar" aria-hidden="true"></span>' +
        '<span class="riw-dd-load-text">Searching<span class="riw-dots"><span></span><span></span><span></span></span></span></div>';
    }
    searchGames(q)
      .then(function (results) {
        if (gen !== searchGen) return;
        if (normalizeQuery(els.search.value) !== q) return;
        els.searchDd.classList.remove('is-stale');
        renderResults(results);
      })
      .catch(function () {
        if (gen !== searchGen) return;
        if (normalizeQuery(els.search.value) !== q) return;
        els.searchDd.classList.remove('is-stale');
        // The rows still up belong to the last search, not this one.
        ddBox().innerHTML = '<div class="riw-dd-empty">Search is unavailable right now. Try again in a moment.</div>';
      });
  }, SEARCH_DEBOUNCE_MS);

  // Scrolls so the search bar sits just under the sticky site header.
  function scrollSearchToTop() {
    var target = els.search.closest('.riw-search-wrap') || els.search;
    var headerH = headerOffset();
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerH - 8;
    if (Math.abs(window.pageYOffset - top) < 2) return;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function bindSearch() {
    if (!els.search) return;
    els.search.addEventListener('input', runSearch);
    // Search on a phone keyboard: put the keyboard away and leave the results showing.
    els.search.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        els.search.blur();
      }
    });
    els.search.addEventListener('focus', function () {
      // Bring the search bar up under the site header so the results below it are visible.
      scrollSearchToTop();
      // Only reopen if there is an actual query — never resume a blank search.
      if (els.search.value.trim() && ddBox() && ddBox().innerHTML.trim()) {
        els.searchDd.hidden = false;
      }
    });
    els.searchDd.addEventListener('mousedown', function (e) {
      var btn = e.target.closest('[data-idx]');
      if (!btn || !els.searchDd.contains(btn)) return;
      e.preventDefault();
      // An in-stock game opens the builder too: another version or condition may still be missing.
      var idx = Number(btn.getAttribute('data-idx'));
      var item = lastResults[idx];
      if (!item) return;
      openBuilder(item);
      // The pick is done with the search box: let it go so a phone keyboard stays down instead of covering the game
      // that just opened under it (owner, 2026-10-22). preventDefault above had kept the box focused.
      els.search.blur();
      // Picked after scrolling down a long list: the list is gone and the game opened up under the search bar, so
      // bring Add a game back on screen with the game under it (owner, 2026-10-23). Not moved if it is already there.
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        scrollUnderHeader(els.search.closest('.riw-search-panel') || els.search, true);
      }
    });
    // Phones: dragging the results puts the keyboard away so the whole list shows and the page scrolls at once.
    els.searchDd.addEventListener(
      'touchmove',
      function () {
        if (document.activeElement === els.search) els.search.blur();
      },
      { passive: true }
    );
    document.addEventListener('click', function (e) {
      if (!els.search.contains(e.target) && !els.searchDd.contains(e.target)) {
        closeDropdown();
      }
    });
  }

  /* ---------- builder ---------- */

  var builder = null; // { primary, system, modern, alternates:[{pcid,title,platform,checked}], conditions:{} }

  /* The builder shows in its own card under the search, or, when a saved game is edited, inside that game's row. */
  function builderHost() {
    return (builder && builder.host) || els.builder;
  }

  function closeBuilder() {
    var inline = builder && builder.host;
    builder = null;
    if (inline) {
      var row = inline.closest('.riw-row');
      if (row) row.classList.remove('is-editing');
      if (inline.parentNode) inline.parentNode.removeChild(inline);
      return;
    }
    els.builder.hidden = true;
    els.builder.innerHTML = '';
  }

  function openBuilder(item, preset) {
    var seed = lastResults.slice();
    if (builder) closeBuilder();
    closeDropdown();
    els.search.value = '';
    lastResults = [];
    if (ddBox()) ddBox().innerHTML = '';
    var system = item.system || item.platformName || '';
    var modern = isModernYear(releaseYear(item));
    builder = {
      primary: {
        pcid: item.pcid,
        title: item.title || item.gameName || '',
        platform: system,
        releaseYear: releaseYear(item),
        imgUrl: item.imgUrl || resultImage(item) || '',
        inStock: !!item.inStock,
        stockConditions: Array.isArray(item.stockConditions) ? item.stockConditions : null,
        productUrl: item.productUrl || ''
      },
      system: system,
      modern: modern,
      alternates: [],
      conditions: defaultConditions(system, modern, item, preset),
      preset: preset || null,
      host: (preset && preset.host) || null
    };
    // Only reveal the card once alternate versions are resolved, so nothing
    // jolts in afterwards. Seed from on-screen results, then complete via the
    // base-name lookup. If that lookup is already cached, render instantly.
    absorbAlternates(item, seed);
    var baseQ = normalizeQuery(stripBrackets(item.title || item.gameName || ''));
    if (!baseQ || searchCache[baseQ]) {
      if (baseQ) absorbAlternates(item, searchCache[baseQ]);
      renderBuilder();
      return;
    }
    renderBuilderLoading();
    searchGames(baseQ)
      .then(function (results) {
        if (!builder || builder.primary.pcid !== item.pcid) return;
        absorbAlternates(item, results);
        renderBuilder();
      })
      .catch(function () {
        if (builder && builder.primary.pcid === item.pcid) renderBuilder();
      });
  }

  /**
   * Which condition chips start on. Editing keeps what the saved game watches. A new game watches every
   * condition we do not have in stock right now; if every condition is in stock, all start on.
   */
  function defaultConditions(system, modern, item, preset) {
    var opts = conditionOptions(system, modern);
    var keep = preset && preset.conditions && opts.some(function (x) {
      return preset.conditions.indexOf(x.key) !== -1;
    });
    var inStock = !keep && item && Array.isArray(item.stockConditions) ? item.stockConditions : [];
    var missing = opts.filter(function (o) {
      return inStock.indexOf(o.key) === -1;
    });
    return opts.reduce(function (set, o) {
      if (keep) set[o.key] = preset.conditions.indexOf(o.key) !== -1;
      else set[o.key] = !missing.length || inStock.indexOf(o.key) === -1;
      return set;
    }, {});
  }

  function stockConditionsOf(entry) {
    return entry && Array.isArray(entry.stockConditions) ? entry.stockConditions : [];
  }

  function renderBuilderLoading() {
    var host = builderHost();
    host.hidden = false;
    host.innerHTML =
      // The sliding line the search list uses, not words (owner, 2026-10-22).
      '<div class="riw-builder-card riw-builder-card--loading" role="status" aria-label="Loading versions">' +
      '<span class="riw-dd-bar" aria-hidden="true"></span>' +
      '</div>';
  }

  function absorbAlternates(item, results) {
    if (!builder || !results || !results.length) return;
    var base = baseName(item.title || item.gameName || '');
    var sysNorm = normPlatform(item.system || item.platformName || '');
    var byPcid = {};
    builder.alternates.forEach(function (a) {
      byPcid[a.pcid] = a;
    });
    byPcid[builder.primary.pcid] = true; // skip primary

    results.forEach(function (v) {
      if (!v.pcid) return;
      if (v.pcid === builder.primary.pcid) {
        // An edited game starts from the saved row, which has no stock: take it from the lookup.
        if (v.inStock) builder.primary.inStock = true;
        if (Array.isArray(v.stockConditions)) builder.primary.stockConditions = v.stockConditions;
        if (v.productUrl && !builder.primary.productUrl) builder.primary.productUrl = v.productUrl;
        return;
      }
      if (baseName(v.title || v.gameName || '') !== base) return;
      if (normPlatform(v.system || v.platformName || '') !== sysNorm) return;

      var existing = byPcid[v.pcid];
      if (existing && existing !== true) {
        // Later search hits can fill in stock/URL the seed missed.
        if (v.inStock) existing.inStock = true;
        if (Array.isArray(v.stockConditions)) existing.stockConditions = v.stockConditions;
        if (v.productUrl) existing.productUrl = v.productUrl;
        return;
      }
      if (existing) return;

      var alt = {
        pcid: v.pcid,
        title: v.title || v.gameName || '',
        platform: v.system || v.platformName || builder.system,
        checked: false,
        inStock: !!v.inStock,
        stockConditions: Array.isArray(v.stockConditions) ? v.stockConditions : null,
        productUrl: v.productUrl || ''
      };
      builder.alternates.push(alt);
      byPcid[v.pcid] = alt;
    });
  }

  /** "In stock now: Loose" for a version we have, linked to its page when it has one. */
  function stockNoteHtml(entry, labelMap, compact) {
    if (!entry || !entry.inStock) return '';
    var have = stockConditionsOf(entry)
      .map(function (k) {
        return labelMap[k] || k;
      })
      .filter(Boolean);
    var text = (compact ? 'In stock' : 'In stock now') + (have.length ? ': ' + have.join(', ') : '');
    if (entry.productUrl) {
      return (
        '<a class="riw-stock-note" href="' +
        escapeHtml(entry.productUrl) +
        '" target="_blank" rel="noopener">' +
        escapeHtml(text) +
        '<span class="riw-stock-note__go">View</span></a>'
      );
    }
    return '<span class="riw-stock-note">' + escapeHtml(text) + '</span>';
  }

  function renderBuilder() {
    if (!builder) return;
    /* Editing a saved game: tick the versions it already watches. */
    if (builder.preset && builder.preset.pcids) {
      builder.alternates.forEach(function (a) {
        a.checked = builder.preset.pcids.indexOf(a.pcid) !== -1;
      });
    }
    var system = builder.system;
    var modern = builder.modern;

    var labelMap = conditionLabelMap(system, modern);

    // Every version can be watched, in stock or not: a game in stock Loose can still be missing boxed.
    var altHtml = builder.alternates
      .map(function (e, i) {
        return (
          '<div class="riw-edition' +
          (e.checked ? ' is-on' : '') +
          '">' +
          '<label class="riw-edition-pick">' +
          '<input type="checkbox" data-alt="' +
          i +
          '"' +
          (e.checked ? ' checked' : '') +
          '>' +
          '<span class="riw-edition-title">' +
          escapeHtml(e.title) +
          '</span>' +
          '</label>' +
          stockNoteHtml(e, labelMap, true) +
          '</div>'
        );
      })
      .join('');

    var primaryStock = stockConditionsOf(builder.primary);
    var stockUrl = builder.primary.productUrl || '';
    var condOptions = conditionOptions(system, modern);
    var allInStock = condOptions.every(function (o) {
      return primaryStock.indexOf(o.key) !== -1;
    });
    var condHtml = condOptions
      .map(function (o) {
        var on = !!builder.conditions[o.key];
        var have = primaryStock.indexOf(o.key) !== -1;
        /* A condition we have now is not one to watch (an alert would fire at once), so it is never a switch that
           can look turned off (grey and crossed out): it is a white chip with a green In stock tag that opens the
           game (owner, 2026-10-22). Only when every condition is in stock do they stay switches, so there is
           still something to pick. */
        if (have && !allInStock) {
          var stockInner =
            '<span class="riw-chip-name">' + escapeHtml(o.label) + '</span>' +
            '<span class="riw-chip-stock">In stock' + (stockUrl ? ' \u00b7 View' : '') + '</span>';
          return stockUrl
            ? '<a class="riw-chip riw-chip--stock" href="' + escapeHtml(stockUrl) + '" target="_blank" rel="noopener">' + stockInner + '</a>'
            : '<span class="riw-chip riw-chip--stock">' + stockInner + '</span>';
        }
        return (
          '<button type="button" class="riw-chip' +
          (on ? ' is-on' : '') +
          '" data-cond="' +
          escapeHtml(o.key) +
          '" aria-pressed="' +
          (on ? 'true' : 'false') +
          '">' +
          escapeHtml(o.label) +
          (have ? '<span class="riw-chip-stock">In stock</span>' : '') +
          '</button>'
        );
      })
      .join('');

    var inline = !!builder.host;
    /* No email and no sign-in note in the picker (owner, 2026-10-22): signed out, the box at the top asks for
       the sign-in. */

    // The picker's cover is larger than a list thumbnail, so it loads sharper.
    var cover = coverThumbUrl(builder.primary.imgUrl, 400);
    var coverHtml = cover
      ? '<span class="riw-builder-img"><img alt="" src="' + escapeHtml(cover) + '" decoding="async"></span>'
      : '';

    var editing = !!(builder.preset && builder.preset.editingId);
    var host = builderHost();
    host.hidden = false;
    /* Editing in its row (owner, 2026-10-22): the row already shows the cover, name and system, so the card
       keeps only the choices, with Save and Cancel at the foot. */
    host.innerHTML =
      '<div class="riw-builder-card' + (inline ? ' riw-builder-card--inline' : '') + '">' +
      (inline
        ? stockNoteHtml(builder.primary, labelMap, false)
        : '<button type="button" class="riw-builder-close" id="riwBuilderClose" aria-label="Cancel">&times;</button>' +
          '<div class="riw-builder-head">' +
          coverHtml +
          '<div class="riw-builder-id">' +
          '<h2 class="riw-builder-title">' +
          escapeHtml(builder.primary.title) +
          '</h2>' +
          '<div class="riw-builder-system">' +
          platformPillHtml(system) +
          '</div>' +
          stockNoteHtml(builder.primary, labelMap, false) +
          '</div></div>') +
      (builder.alternates.length
        ? '<div class="riw-builder-group"><span class="riw-group-label">Also alert me for these versions</span>' +
          '<div class="riw-editions">' +
          altHtml +
          '</div></div>'
        : '') +
      '<div class="riw-builder-group">' +
      '<span class="riw-group-label">Conditions to watch</span>' +
      '<span class="riw-group-hint">' +
      (editing
        ? 'Tap a chip to turn a condition on or off.'
        : primaryStock.length
          ? 'We will email you when a yellow one comes in. Tap a chip to turn it on or off.'
          : 'All conditions are on. Tap a chip to narrow it down.') +
      '</span>' +
      '<div class="riw-chips">' +
      condHtml +
      '</div></div>' +
      (inline
        ? '<div class="riw-inline-actions">' +
          '<button type="button" class="riw-submit riw-submit--save button" id="riwSubmit">Save changes</button>' +
          '<a href="#" role="button" class="riw-inline-cancel" id="riwBuilderClose">Cancel</a>' +
          '</div>'
        : '<button type="button" class="riw-submit' + (editing ? ' riw-submit--save' : '') + ' button" id="riwSubmit">' + (editing ? 'Save changes' : 'Add to wishlist') + '</button>') +
      '<p class="riw-builder-status" id="riwBuilderStatus" role="status"></p>' +
      '</div>';

    bindBuilder();
  }

  function bindBuilder() {
    var host = builderHost();
    var close = document.getElementById('riwBuilderClose');
    if (close) {
      close.addEventListener('click', function (e) {
        e.preventDefault();
        closeBuilder();
      });
    }

    host.querySelectorAll('[data-alt]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var i = Number(cb.getAttribute('data-alt'));
        if (builder.alternates[i]) builder.alternates[i].checked = cb.checked;
        var row = cb.closest('.riw-edition');
        if (row) row.classList.toggle('is-on', cb.checked);
      });
    });

    host.querySelectorAll('[data-cond]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-cond');
        builder.conditions[key] = !builder.conditions[key];
        btn.classList.toggle('is-on', builder.conditions[key]);
        btn.setAttribute('aria-pressed', builder.conditions[key] ? 'true' : 'false');
      });
    });

    var submit = document.getElementById('riwSubmit');
    if (submit) submit.addEventListener('click', onSubmit);
  }

  function selectedConditions() {
    return Object.keys(builder.conditions).filter(function (k) {
      return builder.conditions[k];
    });
  }

  function onSubmit() {
    var status = document.getElementById('riwBuilderStatus');
    var conditions = selectedConditions();
    if (!conditions.length) {
      if (status) status.textContent = 'Pick at least one condition to watch.';
      return;
    }
    var labelMap = conditionLabelMap(builder.system, builder.modern);
    var condLabels = conditions.map(function (k) {
      return labelMap[k] || k;
    });

    var chosen = [builder.primary].concat(
      builder.alternates.filter(function (e) {
        return e.checked;
      })
    );

    var mode = listMode();
    var email = alertEmail();
    if (mode !== 'device' && !isValidEmail(email)) {
      if (status) {
        status.textContent =
          mode === 'link' ? 'Still loading your list. Try again in a moment.' : 'Your account has no email for alerts. Add one in Account.';
      }
      return;
    }

    // Marketing is asked on Shopify's sign-in page; false here changes nothing (the server only records a yes).
    var payload = {
      email: email,
      marketingOptIn: false,
      manageToken: getManageToken() || undefined,
      items: chosen.map(function (e) {
        return { pcid: e.pcid, conditions: conditions };
      })
    };

    // Editing replaces the saved game: drop the old versions and conditions first.
    var editing = builder.preset && builder.preset.editingId ? builder.preset : null;
    var clearOld = Promise.resolve('ok');
    var oldEntry = null;
    if (editing) {
      oldEntry =
        loadLocal().find(function (e) {
          return e.id === editing.editingId;
        }) || null;
      removeLocal(editing.editingId);
      if (mode !== 'device') clearOld = removeOnServer(editing.editingId, editing.pcids || []);
    }

    // Optimistic local mirror so the wishlist view works immediately.
    // All chosen versions collapse into ONE grouped entry (any version fires it).
    var displayName = stripBrackets(builder.primary.title) || builder.primary.title;
    var newId = keyFor(builder.primary.title, builder.system);
    upsertGroup({
      id: newId,
      gameName: displayName,
      platformName: builder.system,
      pcids: chosen.map(function (e) {
        return e.pcid;
      }),
      versions: chosen.map(function (e) {
        return { pcid: e.pcid, title: e.title };
      }),
      // Totals available at add-time so the row can say "Any …" when all are picked.
      versionTotal: 1 + builder.alternates.length,
      condTotal: conditionOptions(builder.system, builder.modern).length,
      conditions: conditions,
      conditionLabels: condLabels,
      imgUrl: builder.primary.imgUrl || '',
      groupId: 'local-' + Date.now(),
      // Saved to the account or the email link's list now, so a later sync must not carry it again.
      // A signed-out add waits here until sign-in carries it.
      synced: mode !== 'device',
      addedAt: Date.now()
    });
    renderList();

    if (mode === 'device') {
      finishSubmit(
        editing ? 'Updated. Sign in to turn on alerts.' : 'Saved. Sign in to turn on alerts.',
        newId
      );
      return;
    }

    if (status) status.textContent = 'Saving…';

    var save = clearOld
      .then(function (cleared) {
        // The old version has to come off first, or the server merges the old completeness into the new.
        if (cleared !== 'ok') {
          return Promise.reject({ editBlocked: cleared });
        }
        return fetch(EP.subscribe, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      })
      .then(function (r) {
        return r.ok
          ? r.json().catch(function () {
              return {};
            })
          : Promise.reject(r.status);
      })
      .then(function (data) {
        // The account email and a Manage link's email are both already proven, so the server turns alerts on.
        if (data && data.status === 'pending') {
          finishSubmit('Saved. Check ' + email + ' for a confirm link to turn on alerts.', newId);
          return;
        }
        finishSubmit(
          (editing ? 'Wishlist updated. Alerts are on for ' : 'Added to your wishlist. Alerts are on for ') + email + '.',
          newId
        );
      })
      .catch(function (err) {
        if (err && err.editBlocked) {
          removeLocal(newId);
          if (oldEntry) upsertGroup(oldEntry);
          renderList();
          finishSubmit(
            err.editBlocked === 'denied'
              ? 'Could not change it: your sign-in or email link has run out. Reload the page and try again.'
              : 'Could not update your wishlist. Nothing changed. Try again.'
          );
          return;
        }
        // Mark it unsaved, so it shows Alerts not on yet and a signed-in visit carries it up again.
        saveLocal(loadLocal().map(function (e) {
          if (e.id === newId) e.synced = false;
          return e;
        }));
        renderList();
        finishSubmit('Could not save it, so alerts are not on yet. Try again.', newId);
      });
    pendingSave = save;
  }

  /**
   * Closes the builder. The card can be taller than the screen, so closing it would leave the page scrolled
   * past the list (near the footer). The saved game's row is brought on screen instead and flashes once.
   */
  function finishSubmit(msg, focusId) {
    var wasInline = !!(builder && builder.host);
    closeBuilder();
    renderWho();
    toast(msg);
    if (!focusId || !els.list) return;
    var row = null;
    els.list.querySelectorAll('.riw-row').forEach(function (r) {
      if (r.getAttribute('data-id') === focusId) row = r;
    });
    if (!row) return;
    if (wasInline) {
      // Edited in place: the row is where the shopper is looking; only bring it on screen if it is not.
      scrollUnderHeader(row, true);
    } else {
      // Phones stack the list under the builder, so the row goes to the top; on a computer the list is beside it.
      var stacked = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
      // A new game is the first row: show the list's heading with it.
      var target = row === els.list.firstElementChild ? els.list.closest('.riw-list-panel') || row : row;
      // Phone: back to Add a game to your wishlist, ready for the next one, with the new row under it (owner,
      // 2026-10-23). scrollUnderHeader leaves 12px above it.
      if (stacked) target = els.search.closest('.riw-search-panel') || target;
      // Signed out on a phone, the yellow Sign in to turn on alerts box sits above the list and would be scrolled
      // past: bring that box to the top instead (owner, iPhone, 2026-10-22).
      if (stacked && els.who && !els.who.hidden && els.who.classList.contains('riw-who--prompt')) target = els.who;
      scrollUnderHeader(target, !stacked);
    }
    row.classList.remove('is-new');
    void row.offsetWidth;
    row.classList.add('is-new');
  }

  /* ---------- wishlist view ---------- */

  function entryTags(entry) {
    var conds = entry.conditions || [];
    if (entry.condTotal && conds.length >= entry.condTotal) {
      return '<span class="riw-tag">Any Completeness</span>';
    }
    var labels = (entry.conditionLabels && entry.conditionLabels.length)
      ? entry.conditionLabels
      : conds;
    return labels
      .map(function (l) {
        return '<span class="riw-tag">' + escapeHtml(l) + '</span>';
      })
      .join('');
  }

  function entryDisplayName(entry) {
    var versions = entry.versions || [];
    // One specific edition (e.g. [Nintendo Selects]) — show its full title, not the stripped base.
    if (versions.length === 1 && versions[0] && versions[0].title) {
      return versions[0].title;
    }
    return entry.gameName || '';
  }

  function listThumbHtml(entry) {
    var src = coverThumbUrl(entry && entry.imgUrl);
    if (!src) return '<span class="riw-row-img riw-row-img--empty"></span>';
    return '<span class="riw-row-img"><img alt="" src="' + escapeHtml(src) + '" decoding="async"></span>';
  }

  function listRowHtml(entry) {
    var versionsHtml = '';
    if (entry.versions && entry.versions.length > 1) {
      var anyVersion = entry.versionTotal && entry.versions.length >= entry.versionTotal;
      if (anyVersion) {
        versionsHtml = '<div class="riw-row-versions"><span class="riw-version-chip">Any Version</span></div>';
      } else {
        versionsHtml =
          '<div class="riw-row-versions">' +
          '<span class="riw-row-versions-label">Versions:</span>' +
          entry.versions
            .map(function (v) {
              return '<span class="riw-version-chip">' + escapeHtml(v.title) + '</span>';
            })
            .join('') +
          '</div>';
      }
    }
    return (
      '<div class="riw-row" data-id="' +
      escapeHtml(entry.id) +
      '">' +
      listThumbHtml(entry) +
      '<div class="riw-row-main">' +
      // Edit and Remove are small icons right after the name (owner, 2026-10-22), not boxed buttons.
      '<div class="riw-row-head">' +
      '<span class="riw-row-title">' +
      escapeHtml(entryDisplayName(entry)) +
      '</span>' +
      // Links acting as buttons, so the theme-wide button chrome (border, shadow) does not box them.
      '<span class="riw-row-actions">' +
      '<a href="#" role="button" class="riw-row-edit" data-edit="' +
      escapeHtml(entry.id) +
      '" aria-label="Edit: change what this alert watches" title="Edit">' +
      '<svg class="riw-row-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 20l1.2-4.6L15.6 5a2 2 0 0 1 2.8 0l.6.6a2 2 0 0 1 0 2.8L8.6 18.8 4 20z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="miter"/><path d="M13.8 6.8l3.4 3.4" fill="none" stroke="currentColor" stroke-width="2.2"/></svg>' +
      '</a>' +
      '<a href="#" role="button" class="riw-row-remove" data-remove="' +
      escapeHtml(entry.id) +
      '" aria-label="Remove from wishlist" title="Remove">' +
      '<svg class="riw-row-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="square"/></svg>' +
      '</a>' +
      '</span>' +
      '</div>' +
      platformPillHtml(entry.platformName) +
      versionsHtml +
      '<div class="riw-row-tags">' +
      // Not saved anywhere yet: signed out, or a signed-in save that has not gone through (retried on the next visit).
      (!entry.synced ? '<span class="riw-tag riw-tag--waiting">Alerts not on yet</span>' : '') +
      entryTags(entry) +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function renderList() {
    if (!els.list) return;
    var entries = loadLocal();
    if (els.listCount) els.listCount.textContent = entries.length ? String(entries.length) : '';
    if (!entries.length) {
      // The search sits beside the list on a computer and above it on a phone, so the line names no side.
      els.list.innerHTML = '<p class="riw-empty">Nothing here yet. Search to add a game.</p>';
      return;
    }
    els.list.innerHTML = entries.map(listRowHtml).join('');
    // A redraw while a row is being edited keeps that row's editor open.
    var editingHost = builder && builder.host;
    if (editingHost) {
      var editingRow = null;
      els.list.querySelectorAll('.riw-row').forEach(function (r) {
        if (r.getAttribute('data-id') === builder.preset.editingId) editingRow = r;
      });
      if (editingRow) {
        editingRow.appendChild(editingHost);
        editingRow.classList.add('is-editing');
      } else {
        builder = null;
      }
    }
    els.list.querySelectorAll('[data-edit]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-edit');
        // The pencil again closes this row's editor.
        if (builder && builder.host && builder.preset && builder.preset.editingId === id) {
          closeBuilder();
          return;
        }
        var entry = loadLocal().find(function (e) {
          return e.id === id;
        });
        if (!entry) return;
        var row = btn.closest('.riw-row');
        if (!row) return;
        // Edits open inside the game's own row (owner, 2026-10-22), not in the card under the search.
        // In the page before it renders, so its buttons can be found by id.
        var host = document.createElement('div');
        host.className = 'riw-row-editor';
        row.appendChild(host);
        var first = (entry.versions && entry.versions[0]) || { pcid: entry.pcids[0], title: entry.gameName };
        openBuilder(
          {
            pcid: first.pcid,
            title: first.title || entry.gameName,
            system: entry.platformName,
            imgUrl: entry.imgUrl || ''
          },
          { editingId: entry.id, pcids: entry.pcids || [], conditions: entry.conditions || null, host: host }
        );
        row.classList.add('is-editing');
      });
    });
    els.list.querySelectorAll('[data-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-remove');
        var entry = loadLocal().find(function (e) {
          return e.id === id;
        });
        removeLocal(id);
        renderList();
        renderWho();
        // A game still waiting on this device was never saved anywhere else.
        if (entry && !entry.synced) return;
        removeOnServer(id, entry ? entry.pcids : []).then(function (result) {
          if (result === 'ok') return;
          if (result === 'denied') {
            if (entry) {
              upsertGroup(entry);
              renderList();
            }
            toast('Could not remove it: your sign-in or email link has run out. Reload the page and try again.');
            return;
          }
          if (entry) {
            upsertGroup(entry);
            renderList();
          }
          toast('Could not remove it from your saved wishlist. Try again.');
        });
      });
    });
  }

  /* ---------- who is this list for ---------- */

  /**
   * The box at the top says where alerts go, and is the one place that asks for a sign-in:
   * - signed in: Signed in as <email>, Sign out.
   * - from an email's Manage link: Alerts go to <that email>, Sign in (prefilled) to keep it in an account.
   * - signed out with games waiting: a yellow box, N games waiting, Sign in to turn on alerts. No news-and-offers
   *   box: Shopify's sign-in page asks that itself.
   * - signed out, nothing waiting: Not signed in, Sign in.
   * Links styled as links: theme-wide button chrome would box a <button>.
   */
  function renderWho() {
    if (!els.who) return;
    var mode = listMode();
    var waiting = mode === 'device' ? waitingEntries().length : 0;
    var html = '';
    if (mode === 'account') {
      var account = String(CUST.email || '').trim();
      html =
        '<p class="riw-who__line"><span class="riw-who__label">Signed in as</span>' +
        '<strong class="riw-who__email">' +
        escapeHtml(account || 'your account') +
        '</strong></p>' +
        // No Account link: the header has one (owner, 2026-10-22).
        '<span class="riw-who__links">' +
        '<a class="riw-who__link" href="' +
        escapeHtml(CFG.logoutUrl || '/account/logout') +
        '">Sign out</a>' +
        '</span>' +
        '<p class="riw-who__sub">Stock alerts go to this email.</p>';
    } else if (mode === 'link') {
      html =
        '<p class="riw-who__line"><span class="riw-who__label">Alerts go to</span>' +
        '<strong class="riw-who__email">' +
        escapeHtml(linkEmail || 'the email this link came to') +
        '</strong></p>' +
        '<a class="riw-who__link" href="' +
        escapeHtml(signInUrl(linkEmail)) +
        '">Sign in</a>' +
        '<p class="riw-who__sub">Sign in to keep this list in your account.</p>';
    } else if (waiting) {
      html =
        '<p class="riw-who__line"><span class="riw-who__label">Alerts not on yet</span>' +
        '<strong class="riw-who__email">' +
        (waiting === 1 ? '1 game waiting' : waiting + ' games waiting') +
        '</strong></p>' +
        '<a class="riw-signin" href="' +
        escapeHtml(signInUrl('')) +
        '">Sign in to turn on alerts</a>';
    } else {
      html =
        '<p class="riw-who__line"><span class="riw-who__label">Not signed in</span></p>' +
        '<a class="riw-who__link" href="' +
        escapeHtml(signInUrl('')) +
        '">Sign in</a>';
    }
    els.who.className = 'riw-who' + (waiting ? ' riw-who--prompt' : '');
    els.who.innerHTML = html;
    els.who.hidden = false;
  }

  function syncFromServer() {
    if (!EP.list) return;
    var manage = getManageToken();
    if (!CUST.loggedIn && !manage) return; // guests without a manage link rely on local mirror
    var url = EP.list + (manage ? '?manage=' + encodeURIComponent(manage) : '');
    fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) return;
        // The server could not tell who this is: keep the local mirror as it is.
        if (data.identity === 'none') return;
        // Fold server rows into grouped entries keyed by base game + platform.
        var keptImages = {};
        loadLocal().forEach(function (entry) {
          if (entry && entry.id && entry.imgUrl) keptImages[entry.id] = entry.imgUrl;
        });
        var byId = {};
        data.items.forEach(function (it) {
          if (!it || typeof it.gameName !== 'string' || !it.gameName.trim()) return;
          var id = it.id || keyFor(it.gameName || '', it.platformName || '');
          var pcids = Array.isArray(it.pcids) ? it.pcids : (it.pcid ? [it.pcid] : []);
          if (!byId[id]) {
            byId[id] = {
              id: id,
              gameName: stripBrackets(it.gameName || '') || it.gameName,
              platformName: it.platformName,
              pcids: [],
              versions: [],
              conditions: [],
              conditionLabels: [],
              groupId: it.groupId,
              addedAt: it.createdAt || Date.now(),
              imgUrl: it.imgUrl || keptImages[id] || ''
            };
          }
          var g = byId[id];
          if (!g.imgUrl && it.imgUrl) g.imgUrl = it.imgUrl;
          g.pcids = uniq(g.pcids.concat(pcids));
          g.conditions = uniq(g.conditions.concat(it.conditions || []));
          g.conditionLabels = uniq(g.conditionLabels.concat(it.conditionLabels || []));
          pcids.forEach(function (pcid) {
            if (g.versions.some(function (v) { return v.pcid === pcid; })) return;
            g.versions.push({
              pcid: pcid,
              title: it.gameName || String(pcid)
            });
          });
        });
        // Games this device added before signing in are not on the account yet. Keep them, and once signed in
        // send them up. Anything that came from the server before (synced) and is gone now was removed on
        // another device, so it is not kept. With a Manage link they stay waiting until a sign-in.
        var onServer = {};
        Object.keys(byId).forEach(function (k) {
          byId[k].synced = true;
          byId[k].pcids.forEach(function (p) {
            onServer[p] = true;
          });
        });
        var waiting = loadLocal().filter(function (entry) {
          if (!entry || entry.synced || byId[entry.id]) return false;
          var pcids = entry.pcids || [];
          return pcids.length > 0 && !pcids.some(function (p) { return onServer[p]; });
        });
        saveLocal(Object.keys(byId).map(function (k) {
          return byId[k];
        }).concat(waiting));
        renderList();
        renderWho();
        if (CUST.loggedIn && waiting.length) carryToAccount(waiting);
      })
      .catch(function () {});
  }

  /** Sends games saved on this device before sign-in to the account, in batches the server accepts. */
  function carryToAccount(entries) {
    var email = CUST.email ? String(CUST.email).trim() : '';
    if (!EP.subscribe || !email) return;
    var batches = [];
    var batch = null;
    entries.forEach(function (entry) {
      var items = (entry.pcids || []).map(function (pcid) {
        return { pcid: pcid, conditions: entry.conditions || [] };
      });
      // A game's versions stay in one request so they land in one group.
      if (!batch || batch.items.length + items.length > CARRY_BATCH) {
        batch = { entries: [], items: [] };
        batches.push(batch);
      }
      batch.entries.push(entry);
      batch.items = batch.items.concat(items.slice(0, CARRY_BATCH));
    });
    var carried = 0;
    pendingSave = batches
      .reduce(function (chain, b) {
        return chain.then(function () {
          return fetch(EP.subscribe, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email, marketingOptIn: getMarketingOptIn(), items: b.items })
          })
            .then(function (r) {
              if (!r.ok) return;
              var ids = {};
              b.entries.forEach(function (entry) {
                ids[entry.id] = true;
              });
              carried += b.entries.length;
              saveLocal(loadLocal().map(function (entry) {
                if (ids[entry.id]) entry.synced = true;
                return entry;
              }));
            })
            .catch(function () {});
        });
      }, Promise.resolve())
      .then(function () {
        renderList();
        if (carried) {
          toast(carried === 1 ? 'Alerts are on for the game you saved.' : 'Alerts are on for the ' + carried + ' games you saved.');
        }
      });
  }

  /* ---------- init ---------- */

  claimLocalMirror();
  renderWho();
  handleQueryFlags();
  loadPrefsPanel();
  bindSearch();
  renderList();
  syncFromServer();
  prefillSearchFromQuery();

  /* A sold-out product page links here with ?q=<title>. Run that search once. */
  function prefillSearchFromQuery() {
    if (!els.search) return;
    var q = '';
    try {
      q = (new URLSearchParams(window.location.search).get('q') || '').trim();
    } catch (e) {}
    if (!q) return;
    els.search.value = q.slice(0, 120);
    els.search.dispatchEvent(new Event('input', { bubbles: true }));
  }
})();
